import { Injectable } from '@angular/core';
import {
  Consultation,
  IOrder,
  Message,
  Prescription,
  PrescriptionDTO,
  UpdatePrescriptionDTO,
  MessageDTO,
  CannedMessageCategory,
  EMR_API,
  IAdminTokenPayload,
  NewConsultationDTO,
  IOrderLineItem,
  IProductItem,
  ICategoryItem,
  IOrderDTO,
  Drug,
  DrugDTO,
  IGetDrugsDTO,
  PrescriptionsQuery,
  IGetConsultationsDTO,
  IGetCustomersDTO,
  UpdateCustomerDTO,
  UpdateConsultationDTO,
  IAdminNotification,
  IAppointment,
  ApplyInvoiceDiscountDTO,
  ICustomerUser,
  ICancelSubscriptionQuery,
  ILabReq,
  ILabReqDTO,
  IChatAuth,
  JoinChatResponse,
  UpdateAppointmentDTO,
  IFile,
  IOrgUser,
  CRMUserFilters,
  GetAppointmentsQueryDto,
  CreateAppointmentDto,
  IAppointmentVirtualMeeting,
  NewCustomerDTO,
  ILead,
  IOrderRequirement,
  INote,
  IGetOrdersQuery,
  IBaseListResponse,
  ISubscription,
  GetTasksDto,
  ITask,
  CreateTaskDto,
  UpdateTaskDto,
  IGetOrdersDTO,
  CannedMessage,
  IProductV2,
  CreateSubscriptionDTO,
  UpdateSubscriptionDTO,
  OrderPrescriptionType,
  LabResult,
  ILabResultDTO,
  LabResultQueryDto,
  LabReqPdfUrlResponse,
  PreAuthResult,
  IFailedWorkflowRunSummary,
  IPharmacy,
  IPharmacyListQuery,
  ICreatePharmacyRequest,
  IUpdatePharmacyRequest,
  ILookup,
  IBelugaVisitSummary,
  IBelugaVisitDrugOption,
  IUpdateBelugaVisitResponse,
  ICancelBelugaVisitResponse
} from '@care-portals/api-interfaces';
import { HttpClient, HttpParams } from '@angular/common/http';
import { environment } from '../../../environments/environment';
import { AuthFacade } from '../../+store/auth/auth.facade';
import { combineLatest, Observable, from } from 'rxjs';
import { convertToHttpParams } from '@care-portals/shared';
import { EMR } from '@care-portals/sdk';
import { AppFacade } from '../../+store/app/app.facade';

interface RequestParams {
  query?: { [param: string]: string | string[] };
  body?: Object;
}
@Injectable()
export class APIService {
  BASE_URL = environment.api.baseUrl;
  user: IAdminTokenPayload;
  token: string;
  organization: string;
  emr: EMR;

  constructor(
    public http: HttpClient,
    public authFacade: AuthFacade,
    public appFacade: AppFacade
  ) {
    this.authFacade.user$.subscribe(res => (this.user = res));

    combineLatest([
      this.authFacade.token$,
      this.appFacade.selectedLayout$
    ]).subscribe(([token, layout]) => {
      if (!token || !layout || !layout.organization) {
        return;
      }
      this.token = token;
      this.organization = layout ? layout.organization : null;
      this.emr = new EMR(this.organization, {
        baseUrl: this.BASE_URL,
        token: this.token
      });
    });
  }

  // DEPRECTATED
  private _requestObs(
    method: string,
    endpoint: string,
    params?: RequestParams
  ) {
    params = params || {};
    const url = this.BASE_URL + endpoint;

    try {
      let response: Observable<any>;
      if (method === 'get' || method === 'delete') {
        response = this.http[method](url, { params: params.query });
      } else {
        // method = POST | PUT  etc..
        response = this.http[method](url, params.body);
      }

      return response;
    } catch (errRes) {
      throw new Error(errRes.error.message);
    }
  }

  ///////////////////////
  ///  ADMIN NOTIFICATIONS
  //////////////////////
  getAdminNotifications(): Observable<IAdminNotification[]> {
    try {
      return this.http.get<IAdminNotification[]>(
        environment.api.baseUrl + EMR_API.notifications.base
      );
    } catch (error) {
      console.error(error);
      throw new Error('Failed to get notifications: ' + error);
    }
  }

  dismissAllNotifications(): Observable<any> {
    try {
      return this.http.put(
        environment.api.baseUrl +
        EMR_API.notifications.base +
        EMR_API.notifications.dismissAll,
        {}
      );
    } catch (error) {
      console.error(error);
      throw new Error('Failed to mark all notification as read: ' + error);
    }
  }

  dismissSingleNotification(notificationId: string): Observable<any> {
    try {
      return this.http.put(
        environment.api.baseUrl +
        EMR_API.notifications.base +
        EMR_API.notifications.dismissSingle.replace(':id', notificationId),
        {}
      );
    } catch (error) {
      console.error(error);
      throw new Error(
        `Failed to mark notification ${notificationId} as read: ` + error
      );
    }
  }

  ////////////////////////
  // CUSTOMERS FUNCTIONS
  ////////////////////////
  getCustomers(
    filters: IGetCustomersDTO = {}
  ): Observable<ICustomerUser[] | IBaseListResponse<ICustomerUser>> {
    return this.http.get<ICustomerUser[] | IBaseListResponse<ICustomerUser>>(
      this.BASE_URL + EMR_API.customers.base,
      { params: filters as any }
    );
  }

  getCustomer(id: string): Observable<ICustomerUser> {
    return this.http.get<ICustomerUser>(
      this.BASE_URL + EMR_API.customers.base + '/' + id
    );
  }

  getCustomerLead(id: string): Observable<ILead> {
    return this.http.get<ILead>(
      this.BASE_URL +
      EMR_API.customers.base +
      EMR_API.customers.lead.replace(':id', id)
    );
  }

  updateCustomer(
    customerId: string,
    customerDTO: UpdateCustomerDTO
  ): Observable<ICustomerUser> {
    return this._requestObs(
      'put',
      EMR_API.customers.base +
      EMR_API.customers.single.replace(':id', customerId),
      {
        body: customerDTO
      }
    );
  }

  searchExternalPharmacies(params: {
    state: string;
    zip?: string;
    name?: string;
    address1?: string;
    address2?: string;
    city?: string;
  }): Observable<ILookup[]> {
    return this.http.get<ILookup[]>(
      this.BASE_URL + EMR_API.customers.base + EMR_API.customers.pharmacySearch,
      { params: convertToHttpParams(params) }
    );
  }

  updateCustomerExtras(
    customerId: string,
    extras: any
  ): Observable<ICustomerUser> {
    return this._requestObs(
      'post',
      EMR_API.customers.base +
      EMR_API.customers.extras.replace(':id', customerId),
      { body: { extras } }
    );
  }

  createCustomer(
    customer: NewCustomerDTO,
    lead?: ILead
  ): Observable<ICustomerUser> {
    return this._requestObs('post', EMR_API.customers.base, {
      body: { customer, lead }
    });
  }

  getCustomerPaymentMethods(id: string): Observable<any> {
    return this.http.get<any>(
      this.BASE_URL +
      EMR_API.customers.base +
      EMR_API.customers.paymentMethods.replace(':id', id)
    );
  }

  updateWatchlist(customerId: string, isAdd: boolean, userId?: string) {
    return this.http.put<string[]>(
      this.BASE_URL +
      EMR_API.customers.base +
      EMR_API.customers.watchlist.replace(':id', customerId),
      { isAdd, userId }
    );
  }

  //////////////////////
  ///  TASKS
  //////////////////////
  getTasks(filters: GetTasksDto): Observable<IBaseListResponse<ITask>> {
    return this.http.get<IBaseListResponse<ITask>>(
      this.BASE_URL + EMR_API.tasks.base,
      {
        params: convertToHttpParams(filters)
      }
    );
  }

  getTaskById(id: string): Observable<ITask> {
    return this.http.get<ITask>(this.BASE_URL + EMR_API.tasks.base + '/' + id);
  }

  createTask(task: CreateTaskDto): Observable<ITask> {
    return this.http.post<ITask>(this.BASE_URL + EMR_API.tasks.base, task);
  }

  updateTask(id: string, task: UpdateTaskDto): Observable<ITask> {
    return this.http.put<ITask>(
      this.BASE_URL + EMR_API.tasks.base + '/' + id,
      task
    );
  }

  deleteTask(id: string): Observable<any> {
    return this.http.delete(this.BASE_URL + EMR_API.tasks.base + '/' + id);
  }

  ////////////////
  /// ORDERS
  ///////////////
  getOrder(orderId: number): Observable<IOrder> {
    return this.http.get<IOrder>(
      this.BASE_URL +
      EMR_API.orders.base +
      EMR_API.orders.single.replace(':orderId', orderId.toString())
    );
  }

  getAllOrders(
    filter?: IGetOrdersDTO
  ): Observable<IOrder[] | IBaseListResponse<IOrder>> {
    // remove nulls and undefined properties from the request
    filter = Object.fromEntries(
      Object.entries(filter).filter(([_, v]) => v != null)
    );

    return this.http.get<IOrder[]>(this.BASE_URL + EMR_API.orders.base, {
      params: { ...filter } as HttpParams
    });
  }

  getAllArchivedOrders(
    filter?: IGetOrdersQuery
  ): Observable<IOrder[] | IBaseListResponse<IOrder>> {
    // remove nulls and undefined properties from the request
    filter = Object.fromEntries(
      Object.entries(filter).filter(([_, v]) => v != null)
    );

    return this.http.get<IOrder[]>(
      this.BASE_URL + EMR_API.orders.base + EMR_API.orders.archived,
      {
        params: { ...filter } as HttpParams
      }
    );
  }

  createOrder(
    items: IOrderLineItem[],
    customerId: string,
    isRefill: boolean,
    delayedCapture?: boolean,
    sendEvent?: boolean
  ): Observable<any> {
    return this._requestObs('post', EMR_API.orders.base, {
      body: { items, customerId, isRefill, delayedCapture, sendEvent }
    });
  }

  updateOrder(orderId: number, data: IOrderDTO): Observable<IOrder> {
    return this._requestObs(
      'put',
      EMR_API.orders.base +
      EMR_API.orders.single.replace(':orderId', '' + orderId),
      {
        body: { data }
      }
    );
  }

  retriggerStatus(orderId: number, type: 'order' | 'payment'): Observable<IOrder> {
    return this.http.put<IOrder>(
      this.BASE_URL + EMR_API.orders.base + EMR_API.orders.retrigger,
      { id: orderId, type }
    );
  }

  applyDiscountForInvoice(body: ApplyInvoiceDiscountDTO) {
    return this.http.post<any>(
      this.BASE_URL + EMR_API.orders.base + EMR_API.orders.singleDiscount,
      body
    );
  }

  chargeOrder(orderId: string, customerId: string) {
    return this.http.post<any>(
      this.BASE_URL +
      EMR_API.orders.base +
      EMR_API.orders.payments.replace(':orderId', orderId),
      {}
    );
  }

  refillItem(orderId: number, product: IProductItem) {
    return this.http.post(
      this.BASE_URL +
      EMR_API.orders.base +
      EMR_API.orders.refillItem
        .replace(':orderId', '' + orderId)
        .replace(':productId', '' + product.id),
      {}
    );
  }

  exportOrders(filter?: IGetOrdersDTO): Observable<string> {
    const params = Object.fromEntries(
      Object.entries(filter ?? {}).filter(([_, v]) => v != null)
    );

    return this.http.get(
      this.BASE_URL + EMR_API.orders.base + EMR_API.orders.exportCsv,
      { params, responseType: 'text' }
    );
  }

  exportSubscriptions(dateRange: [Date, Date]): Observable<any> {
    const params = new HttpParams()
      .set('dateFrom', dateRange[0].toISOString())
      .set('dateTo', dateRange[1].toISOString());

    return this.http.get(
      this.BASE_URL + EMR_API.subscriptions.base + EMR_API.orders.exportCsv,
      { params, responseType: 'text' }
    );
  }

  getOrderPrescriptions(id: string): Observable<{
    prescriptions: Prescription[];
    type: OrderPrescriptionType;
  }> {
    return this._requestObs(
      'get',
      EMR_API.orders.base + EMR_API.orders.prescriptions.replace(':orderId', id)
    );
  }

  getOrderFailedWorkflowRuns(
    id: string,
    orderMongoId?: string
  ): Observable<IFailedWorkflowRunSummary[]> {
    return this._requestObs(
      'get',
      EMR_API.orders.base +
      EMR_API.orders.failedWorkflowRuns.replace(':orderId', id),
      orderMongoId ? { query: { orderMongoId } } : undefined
    );
  }

  getSubscription(id: string): Observable<ISubscription> {
    return this._requestObs('get', EMR_API.subscriptions.base + `/${id}`);
  }

  //////////////////
  /// CONSULTATIONS
  /////////////////
  getConsultations(
    filters: IGetConsultationsDTO = {}
  ): Observable<Consultation[] | IBaseListResponse<Consultation>> {
    return this._requestObs('get', EMR_API.consultations.base, {
      query: filters as any
    });
  }

  getOrderRequirements(orderId: number): Observable<IOrderRequirement[]> {
    return this._requestObs(
      'get',
      EMR_API.orders.base +
      EMR_API.orders.singleRequirements.replace(':orderId', String(orderId))
    );
  }

  getOrderVisit(orderId: number | string): Observable<IBelugaVisitSummary> {
    return this.http.get<IBelugaVisitSummary>(
      this.BASE_URL +
      EMR_API.orders.base +
      EMR_API.orders.visit.replace(':orderId', String(orderId))
    );
  }

  getOrderVisitDrugs(orderId: number | string): Observable<IBelugaVisitDrugOption[]> {
    return this.http.get<IBelugaVisitDrugOption[]>(
      this.BASE_URL +
      EMR_API.orders.base +
      EMR_API.orders.visitDrugs.replace(':orderId', String(orderId))
    );
  }

  updateOrderVisit(
    orderId: number | string,
    medIds: string[]
  ): Observable<IUpdateBelugaVisitResponse> {
    return this.http.put<IUpdateBelugaVisitResponse>(
      this.BASE_URL +
      EMR_API.orders.base +
      EMR_API.orders.visit.replace(':orderId', String(orderId)),
      { medIds }
    );
  }

  cancelOrderVisit(
    orderId: number | string
  ): Observable<ICancelBelugaVisitResponse> {
    return this.http.delete<ICancelBelugaVisitResponse>(
      this.BASE_URL +
      EMR_API.orders.base +
      EMR_API.orders.visit.replace(':orderId', String(orderId))
    );
  }

  getLatestConsultation(
    customerId: string,
    type: string
  ): Observable<Consultation[]> {
    return this._requestObs(
      'get',
      EMR_API.consultations.base + EMR_API.consultations.latest,
      { query: { customerId, type } }
    );
  }

  getSatisfiedConsultations(customerId: string): Observable<Consultation[]> {
    return this._requestObs(
      'get',
      EMR_API.consultations.base + EMR_API.consultations.satisfied,
      { query: { customerId } }
    );
  }

  saveConsultation(consultation: NewConsultationDTO): Observable<Consultation> {
    return this._requestObs('post', EMR_API.consultations.base, {
      body: consultation
    });
  }

  updateConsultation(
    id: string,
    consultation: UpdateConsultationDTO
  ): Observable<Consultation> {
    return this._requestObs('put', EMR_API.consultations.base + '/' + id, {
      body: consultation
    });
  }

  getPathwaiViewerToken(
    consultationId: string
  ): Observable<{
    success: boolean;
    data: { token: string; expiresIn: number };
  }> {
    return this._requestObs(
      'get',
      EMR_API.consultations.base +
      EMR_API.consultations.viewerToken.replace(':id', consultationId)
    );
  }

  //////////////////
  /// NOTES
  //////////////////
  getNotes(filters?): Observable<INote[]> {
    return this._requestObs('get', EMR_API.notes.base, { query: filters });
  }

  saveNote(note: Partial<INote>): Observable<INote> {
    return this._requestObs('post', EMR_API.notes.base, { body: note });
  }

  updateNote(note: Partial<INote>): Observable<INote> {
    return this._requestObs(
      'put',
      EMR_API.notes.base + EMR_API.notes.single.replace(':id', note._id),
      { body: note }
    );
  }

  //////////////////
  /// FILES
  //////////////////
  private buildFileUrl(
    customerId: string,
    fileName: string,
    folder?: string
  ): string {
    let url = `${EMR_API.customers.base}/${customerId}/files/${encodeURIComponent(fileName)}`;
    if (folder) {
      url += `?folder=${encodeURIComponent(folder)}`;
    }
    return url;
  }

  getFiles(
    customerId: string,
    folder?: string,
    options?: { fromDate?: string; toDate?: string }
  ): Observable<IFile[]> {
    let url =
      EMR_API.customers.base +
      EMR_API.customers.files.replace(':id', customerId);
    const params = new URLSearchParams();
    if (folder) params.set('folder', folder);
    if (options?.fromDate) params.set('fromDate', options.fromDate);
    if (options?.toDate) params.set('toDate', options.toDate);
    const qs = params.toString();
    if (qs) url += `?${qs}`;
    return this._requestObs('get', url);
  }

  getFile(
    customerId: string,
    fileName: string,
    folder?: string
  ): Observable<IFile> {
    const base = this.buildFileUrl(customerId, fileName, folder);
    const url = base.includes('?') ? `${base}&url=true` : `${base}?url=true`;
    return this._requestObs('get', url);
  }

  uploadFile(customerId: string, file: IFile, folder?: string): Observable<any> {
    return this._requestObs('put', this.buildFileUrl(customerId, file.name, folder), {
      body: { ...file }
    });
  }

  deleteFile(
    customerId: string,
    resourceName: string,
    folder?: string
  ): Observable<any> {
    return this._requestObs(
      'delete',
      this.buildFileUrl(customerId, resourceName, folder)
    );
  }

  //////////////////////
  ///  PRESCRIPTIONS
  ////////////////////////
  getPrescription(id: string): Observable<Prescription> {
    return this._requestObs('get', EMR_API.prescriptions.base + '/' + id, {
      query: { print: 'true' }
    });
  }

  getAllPrescriptions(
    filters?: PrescriptionsQuery
  ): Observable<Prescription[] | IBaseListResponse<Prescription>> {
    return this._requestObs('get', EMR_API.prescriptions.base, {
      query: filters as any
    });
  }

  createPrescription(prescription: PrescriptionDTO): Observable<Prescription> {
    return this._requestObs('post', EMR_API.prescriptions.base, {
      body: prescription
    });
  }

  updatePrescription(
    id: string,
    prescription: UpdatePrescriptionDTO
  ): Observable<Prescription> {
    return this._requestObs('put', EMR_API.prescriptions.base + '/' + id, {
      body: prescription
    });
  }

  addFulfillment(id: string, fulfillment: any): Observable<Prescription> {
    return this._requestObs(
      'post',
      EMR_API.prescriptions.base + '/' + id + '/fulfillments',
      {
        body: fulfillment
      }
    );
  }

  //////////////////////
  ///  DRUGS
  ////////////////////////
  getDrugs(
    filters?: IGetDrugsDTO
  ): Observable<Drug[] | IBaseListResponse<Drug>> {
    return this._requestObs('get', EMR_API.drugs.base, {
      query: convertToHttpParams(filters)
    });
  }

  getDrugByDin(din: string): Observable<Drug> {
    return this._requestObs('get', `${EMR_API.drugs.base}/${encodeURIComponent(din)}`);
  }

  saveDrug(drug: DrugDTO): Observable<Drug> {
    return this._requestObs('post', EMR_API.drugs.base, {
      body: drug
    });
  }

  updateDrug(id: string, drug: DrugDTO): Observable<Drug> {
    return this._requestObs('put', EMR_API.drugs.base + '/' + id, {
      body: drug
    });
  }

  /////////////////
  /// PRODUCTS
  /////////////////

  getProductVariants(productId: string): Observable<any[]> {
    return this._requestObs(
      'get',
      EMR_API.productsv2.base +
      EMR_API.productsv2.variants.replace(':productId', productId)
    );
  }

  getCategories(): Observable<ICategoryItem[]> {
    return this._requestObs(
      'get',
      EMR_API.productsv2.base + EMR_API.productsv2.categories
    );
  }

  getProductsInCategory(category?: string): Observable<IProductV2[]> {
    return this._requestObs(
      'get',
      EMR_API.productsv2.base + (category ? '/' + category : '')
    );
  }

  getProductV2(id: string): Observable<IProductV2> {
    return this._requestObs(
      'get',
      EMR_API.productsv2.base + EMR_API.productsv2.single.replace(':id', id)
    );
  }

  //////////////////
  ///  MESSAGES
  /////////////////

  getMessages(filters: any): Observable<Message[]> {
    return this._requestObs(
      'get',
      EMR_API.messages.base + EMR_API.messages.list,
      { query: filters }
    );
  }

  sendMessage(message: MessageDTO): Observable<Message> {
    return this._requestObs(
      'post',
      EMR_API.messages.base + EMR_API.messages.outbound,
      { body: message }
    );
  }

  sendRx(rxId: string) {
    return this.http.post<any>(
      this.BASE_URL +
      EMR_API.prescriptions.base +
      EMR_API.prescriptions.fax.replace(':id', rxId),
      {}
    );
  }

  sendPrescriptionPdfToPatientChat(prescriptionId: string) {
    return this.http.post<{ success: boolean; messageId?: string }>(
      this.BASE_URL +
      EMR_API.prescriptions.base +
      EMR_API.prescriptions.sendPdfToChat.replace(':id', prescriptionId),
      {}
    );
  }

  getPrescriptionPdfSignedUrl(
    prescriptionId: string
  ): Observable<{ success: boolean; signedUrl?: string }> {
    return this.http.get<{ success: boolean; signedUrl?: string }>(
      this.BASE_URL +
      EMR_API.prescriptions.base +
      EMR_API.prescriptions.pdf.replace(':id', prescriptionId)
    );
  }

  uploadInboxFile(
    customerId: string,
    filename: string,
    base64: string
  ): Observable<any> {
    return this.http.put<any>(
      this.BASE_URL + EMR_API.messages.base + EMR_API.messages.upload,
      { customerId, filename, data: base64 }
    );
  }

  sendInvoiceToPatient(invoiceId, toEmail) {
    return this.http.post<any>(
      this.BASE_URL + EMR_API.invoices.base + EMR_API.invoices.sendToPatient,
      { toEmail, invoiceId }
    );
  }

  /////////////////////
  ///  CANNED-MESSAGES
  ////////////////////

  getCannedMessages(filters: any): Observable<CannedMessage[]> {
    const params = convertToHttpParams(filters);
    return this.http.get<CannedMessage[]>(
      this.BASE_URL + EMR_API.cannedMessages.base,
      {
        params
      }
    );
  }

  addCannedMessage(message: any): Observable<any> {
    return this._requestObs('post', EMR_API.cannedMessages.base, {
      body: message
    });
  }

  updateCannedMessage(message: any): Observable<any> {
    return this._requestObs(
      'put',
      EMR_API.cannedMessages.base +
      EMR_API.cannedMessages.single.replace(':id', message._id),
      { body: message }
    );
  }

  deleteCannedMessage(id: string): Observable<any> {
    return this._requestObs(
      'delete',
      EMR_API.cannedMessages.base +
      EMR_API.cannedMessages.single.replace(':id', id)
    );
  }

  getCannedMessageCategories(): Observable<CannedMessageCategory[]> {
    return this.http.get<CannedMessageCategory[]>(
      this.BASE_URL +
      EMR_API.cannedMessages.base +
      EMR_API.cannedMessages.categories
    );
  }

  addCannedMessageCategory(name: string): Observable<any> {
    return this._requestObs(
      'post',
      EMR_API.cannedMessages.base + EMR_API.cannedMessages.categories,
      { body: { name } }
    );
  }

  updateCannedMessageCategory(id: string, name: string): Observable<any> {
    return this._requestObs(
      'put',
      EMR_API.cannedMessages.base +
      EMR_API.cannedMessages.categorySingle.replace(':id', id),
      { body: { name } }
    );
  }

  deleteCannedMessageCategory(id: string): Observable<any> {
    return this._requestObs(
      'delete',
      EMR_API.cannedMessages.base +
      EMR_API.cannedMessages.categorySingle.replace(':id', id)
    );
  }

  ///////////////////////
  ///  Appointments
  //////////////////////
  getAppointmentById(id: string) {
    return this.http.get<IAppointment>(
      this.BASE_URL + EMR_API.appointments.base + `/${id}`
    );
  }

  getAppointments(query: GetAppointmentsQueryDto): Observable<IAppointment[]> {
    const params = convertToHttpParams(query);
    return this.http.get<IAppointment[]>(
      this.BASE_URL + EMR_API.appointments.base,
      {
        params
      }
    );
  }

  getMySeenAppointments(
    seenAtDay: string,
    brand: string
  ): Observable<IAppointment[]> {
    return this.http.get<IAppointment[]>(
      this.BASE_URL + EMR_API.appointments.base + EMR_API.appointments.mySeen,
      {
        params: {
          seenAtDay: seenAtDay,
          brand
        }
      }
    );
  }

  createAppointment(
    appointment: CreateAppointmentDto
  ): Observable<IAppointment> {
    return this.http.post<IAppointment>(
      this.BASE_URL + EMR_API.appointments.base,
      appointment
    );
  }

  /**
   * Pre-Auth (JJC only). No `din` → server returns candidate drugs to confirm;
   * with `din` → server creates the OkRx form and returns { url }.
   */
  createPreAuth(
    customerId: string,
    din?: string
  ): Observable<PreAuthResult> {
    return this.http.post<PreAuthResult>(
      this.BASE_URL + EMR_API.preAuth.base + EMR_API.preAuth.create,
      { customerId, din }
    );
  }

  updateAppointment(
    id: string,
    appointment: UpdateAppointmentDTO
  ): Observable<IAppointment> {
    return this.http.put<IAppointment>(
      this.BASE_URL + EMR_API.appointments.base + '/' + id,
      appointment
    );
  }

  pullAppointment(): Observable<IAppointment> {
    return this.http.put<IAppointment>(
      this.BASE_URL + EMR_API.appointments.base + EMR_API.appointments.pull,
      null
    );
  }

  //////////////////
  ///  SUBSCRIPTIONS
  /////////////////

  getSubscriptions(query: any) {
    return this.http.get<IBaseListResponse<ISubscription>>(
      this.BASE_URL + EMR_API.subscriptions.base,
      {
        params: query
      }
    );
  }

  createSubscription(payload: Partial<CreateSubscriptionDTO>) {
    const body = {
      ...payload,
      nextCycleDate:
        payload.nextCycleDate instanceof Date
          ? payload.nextCycleDate.toISOString()
          : payload.nextCycleDate
    };
    return this.http.post<ISubscription>(
      this.BASE_URL + EMR_API.subscriptions.base,
      body
    );
  }

  cancelSubscription(id: string, params: ICancelSubscriptionQuery = {}) {
    return this.http.delete<ISubscription>(
      this.BASE_URL +
      EMR_API.subscriptions.base +
      EMR_API.subscriptions.single.replace(':id', id),
      {
        params: params as HttpParams
      }
    );
  }

  updateSubscription(id: string, updates: UpdateSubscriptionDTO) {
    return this.http.put<ISubscription>(
      environment.api.baseUrl +
      EMR_API.subscriptions.base +
      EMR_API.subscriptions.single.replace(':id', id),
      updates
    );
  }

  //////////////////
  ///  INVOICES
  /////////////////
  getInvoices(query: any) {
    return this.http.get<any[]>(this.BASE_URL + EMR_API.invoices.base, {
      params: query
    });
  }

  //////////////////////
  ///  Lab Req
  ////////////////////////
  getLabReqs(filters?: any): Observable<ILabReq[]> {
    filters = Object.fromEntries(
      Object.entries(filters).filter(([_, v]) => v != null)
    );
    return this._requestObs('get', EMR_API.labReq.base, {
      query: filters
    });
  }

  saveLabReq(labReq: ILabReqDTO): Observable<ILabReq> {
    return this._requestObs('post', EMR_API.labReq.base, {
      body: labReq
    });
  }

  updateLabReq(id: string, labReq: ILabReqDTO): Observable<ILabReq> {
    return this._requestObs('put', EMR_API.labReq.base + '/' + id, {
      body: labReq
    });
  }

  getLabReqPdfUrl(id: string): Observable<LabReqPdfUrlResponse> {
    return this._requestObs('get', EMR_API.labReq.base + '/' + id + '/pdf', {});
  }

  //////////////////
  /// ENCOUNTERS
  //////////////////

  startChat(customerId: string): Observable<JoinChatResponse> {
    return this._requestObs('put', EMR_API.chat.base + EMR_API.chat.join, {
      body: {
        customerId
      }
    });
  }

  refreshToken(token: string): Observable<IChatAuth> {
    return this._requestObs(
      'put',
      EMR_API.chat.base + EMR_API.chat.refreshToken,
      { body: { token } }
    );
  }

  forwardEncounter(customerId: string, to: 'CS' | 'PH') {
    return this._requestObs(
      'post',
      EMR_API.messages.base + EMR_API.messages.forward,
      { body: { to, customerId } }
    );
  }

  joinVideo(customerId: string): Observable<IAppointmentVirtualMeeting> {
    return this._requestObs(
      'put',
      EMR_API.appointments.base + EMR_API.appointments.meeting,
      {
        body: {
          customerId
        }
      }
    );
  }

  ////////////////////////
  // Admin Users (from CRM) FUNCTIONS
  ////////////////////////
  getAdminUsers(filters: CRMUserFilters = {}) {
    return this.http.get<IOrgUser[]>(this.BASE_URL + EMR_API.users.base, {
      params: { ...filters }
    });
  }

  /**
   * Active pharmacies only — call with no args for selection dropdowns so disabled
   * pharmacies cannot be picked. Pass `{ includeDisabled: true }` when the full list
   * is needed (name lookups for already-assigned pharmacies).
   */
  getPharmacies(query: IPharmacyListQuery = {}) {
    return this.http.get<IPharmacy[]>(this.BASE_URL + EMR_API.pharmacies.base, {
      params: query.includeDisabled ? { includeDisabled: 'true' } : {}
    });
  }

  getPharmacy(id: string) {
    return this.http.get<IPharmacy>(
      this.BASE_URL + EMR_API.pharmacies.base + EMR_API.pharmacies.single.replace(':id', id)
    );
  }

  createPharmacy(payload: ICreatePharmacyRequest) {
    return this.http.post<IPharmacy>(
      this.BASE_URL + EMR_API.pharmacies.base,
      payload
    );
  }

  updatePharmacy(id: string, payload: IUpdatePharmacyRequest) {
    return this.http.put<IPharmacy>(
      this.BASE_URL + EMR_API.pharmacies.base + EMR_API.pharmacies.single.replace(':id', id),
      payload
    );
  }

  deletePharmacy(id: string) {
    return this.http.delete<{ success: boolean }>(
      this.BASE_URL + EMR_API.pharmacies.base + EMR_API.pharmacies.single.replace(':id', id)
    );
  }

  getInvoiceEmailTemplate(invoiceId: string) {
    return this.http.get<{ label: string; html: string; _id: string }>(
      this.BASE_URL +
      EMR_API.emailTemplates.base +
      EMR_API.emailTemplates.invoice,
      { params: { invoiceId } }
    );
  }

  updateCustomerCredit(
    customerId: string,
    amount: number,
    note?: string,
    orderId?: number
  ): Observable<ICustomerUser> {
    return this.http.put<ICustomerUser>(
      `${this.BASE_URL}${EMR_API.customers.base
      }${EMR_API.customers.credits.replace(':id', customerId)}`,
      { amount, note, orderId }
    );
  }

  ////////////////////////
  // Lab Results FUNCTIONS
  ////////////////////////

  getLabResult(labResultId: string): Observable<LabResult> {
    return from(this.emr.getLabResult(labResultId));
  }

  getLabResults(filters?: Partial<LabResultQueryDto>): Observable<LabResult[]> {
    filters = Object.fromEntries(
      Object.entries(filters || {}).filter(([_, v]) => v != null)
    ) as Partial<LabResultQueryDto>;
    return from(this.emr.getLabResults(filters));
  }

  getLabResultsByCustomer(
    filters: Partial<LabResultQueryDto>,
    customerId: string
  ): Observable<LabResult[]> {
    return from(this.emr.getLabResultsByCustomer(filters, customerId));
  }

  updateLabResult(
    labResultId: string,
    data: Partial<LabResult>
  ): Observable<LabResult> {
    return from(this.emr.updateLabResult(labResultId, data));
  }

  createLabResult(
    customerId: string,
    labResult: ILabResultDTO,
    file?: { buffer: string; name: string; mimetype: string }
  ): Observable<LabResult> {
    return from(this.emr.createLabResult(customerId, labResult, file));
  }

  submitLabResultPdf(
    customerId: string,
    file: { buffer: string; name: string; mimetype: string }
  ): Observable<{ faxId: string; fileName: string }> {
    return from(this.emr.submitLabResultPdf(customerId, file));
  }

  getLabResultPdfSignedUrl(
    labResultId: string
  ): Observable<{ success: boolean; signedUrl?: string }> {
    return from(this.emr.getLabResultPdfSignedUrl(labResultId));
  }

}
